import { CompanyDto } from 'api/companies/models/CompanyDto';
import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import Loading from 'common/services/Loading';
import { useHistory, useParams } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import CompaniesService from 'api/companies/CompaniesService';
import { DEFAULT_EMAIL_RULES_WITH_REQUIRED, DEFAULT_INPUT_RULES, DEFAULT_INPUT_RULES_WITH_REQUIRED, LOGGER_LOG_TYPE, removeAccents } from 'Config';
import Logger from 'common/services/Logger';
import ScreenTitle from 'common/components/screenTitle/ScreenTitle';
import { CompactPageLayout, compactPageLayoutStyles } from 'common/components/compactPageLayout';
import { Col, Row } from 'react-flexbox-grid';
import FormItem from 'common/components/formItem/FormItem';
import Label from 'common/components/label/Label';
import Input from 'common/components/input/Input';
import InputError from 'common/components/inputError/InputError';
import styles from './CompanyScreen.module.scss';
import { useFieldArray, useForm } from 'react-hook-form';
import Box from 'common/components/box/Box';
import Button from 'common/components/button/Button';
import QuestionYesNo from 'common/components/questionYesNo/QuestionYesNo';
import CustomFile from 'common/models/CustomFile';
import SelectController from 'common/components/select/SelectController'; 
import Separator from 'common/components/separator/Separator';
import { useSelector } from 'react-redux';
import { Reducers } from 'store/types';
import { UserProfile } from 'api/account/models/UserProfile';
import UsersService from 'api/users/UsersService';
import CheckBoxController from 'common/components/checkBox/CheckBoxController';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import Tabs, { TabItem } from 'common/components/tabs/Tabs';
import PlansScreen from 'screens/account/components/PlansScreen';
import CountriesService from 'api/countries/CountriesService';
import AddIcon from 'assets/svg/desktop_add_new.svg';
import { PackCompanyDto } from 'api/packs/models/PackDto';
import PlanFormItem from './components/PlanFormItem';
import LanguagesService from 'api/languages/LanguagesService';
import RulesTab from 'screens/security/components/rulesTab/RulesTab';
import RolesList from 'screens/security/components/rolesTab/components/rolesList/RolesList';
import RolesDetails from 'screens/security/components/rolesTab/components/rolesDetails/RolesDetails';
import GpsLocatorFormItem from './components/GpsLocatorFormItem';
import { GpsLocatorCompanyDto } from 'api/gpsLocators/models/GpsLocatorDto';
import AdditionalDataList from 'screens/additional-data/additionalDataList/AdditionalDataList';
import AdditionalDataDetails from 'screens/additional-data/additionalDataDetails/AdditionalDataDetails';
import SmsFormItem from './components/SmsFormItem';
import DashboardGroupField from './components/DashboardGroupField';
import { TextMessagesPurchaseDto } from 'api/textMessagesPurchases/models/TextMessagesPurchaseDto';
import { PackTypesEnum } from 'api/packs/enums/PackTypesEnum';
import { FaInfoCircle } from 'react-icons/fa';
import ReactTooltip from 'react-tooltip';

export type CloseType = 'edit' | 'details';


export interface PlanItem {
    label: string;
    value: string;
    id: string;
}

const CompanyScreen = () => {
    const { id, type, tabId, tabType, childrenId } = useParams<{ id: string, type: string, tabId: string, tabType: string, childrenId: string }>();
    const { t } = useTranslation();
    const { addToast } = useToasts();
    const history = useHistory();
    const [item, setItem] = useState<CompanyDto>({ id: '', name: '', useValueWithVat: true, packs: [], languageId: '', gpsLocators: [] });
    const companyForm = useForm<CompanyDto>({ shouldUnregister: false, defaultValues: id == undefined ? { id: '', name: '', useValueWithVat: true, packs: [{}], gpsLocators: [] } : undefined });
    const { register, handleSubmit, errors, watch, clearErrors } = companyForm;
    const [medias, setMedias] = useState<CustomFile[]>([]);
    const isDetails = type === 'details';
    const [showRemoveModal, setShowRemoveModal] = useState<boolean>(false);
    const [showRemovePlanModal, setShowRemovePlanModal] = useState<boolean>(false);

    const [itemToRemove, setItemToRemove] = useState<CompanyDto | null>(null);
    const [planToRemove, setPlanToRemove] = useState<number | null>(null);

    const [countries, setCountries] = useState<SelectValueLabel[]>([]);
    const [languages, setLanguages] = useState<SelectValueLabel[] | null>([]);

    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const hasCompaniesWritePolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_COMPANIES_WRITE']);
    const [activeTabId, setActiveTabId] = useState<string>('account')

    const [plansList, setPlansList] = useState<PlanItem[]>([])
    const [reRender, setReRender] = useState<number>(0);
    const [hasLoaded, setHasLoaded] = useState<boolean>(false);
    const packs = useFieldArray<PackCompanyDto>({ control: companyForm.control, name: 'packs', keyName: 'formId' as any });
    const gpsLocators = useFieldArray<GpsLocatorCompanyDto>({ control: companyForm.control, name: 'gpsLocators', keyName: 'formId' as any });
    const textMessagesPurchases = useFieldArray<TextMessagesPurchaseDto>({ control: companyForm.control, name: 'textMessagesPurchases', keyName: 'formId' as any });

    const [gpsLocatorList, setGpsLocatorList] = useState<SelectValueLabel[]>([]);
    const [showRemoveSmsPurchaseModal, setShowRemoveSmsPurchaseModal] = useState<boolean>(false);
    const [smsPurchaseToRemove, setSmsPurchaseToRemove] = useState<number | null>(null);
    const watchedTextMessagesPurchases = watch('textMessagesPurchases');
    const watchedPacks = watch('packs');
    const watchedReservationType = watch('reservationType');

    const reservationTypeOptions: SelectValueLabel[] = [
        { value: 'DIRECT', label: t('requests.config.type_direct') as string },
        { value: 'WORKFLOW_WITH_ASSET', label: t('requests.config.type_workflow_with_asset') as string },
        { value: 'WORKFLOW_WITHOUT_ASSET', label: t('requests.config.type_workflow_without_asset') as string }
    ];

    const getReservationTypeDescription = (type?: string): string => {
        switch (type) {
            case 'DIRECT':
                return t('requests.config.type_direct_desc') as string;
            case 'WORKFLOW_WITH_ASSET':
                return t('requests.config.type_workflow_with_asset_desc') as string;
            case 'WORKFLOW_WITHOUT_ASSET':
                return t('requests.config.type_workflow_without_asset_desc') as string;
            default:
                return '';
        }
    };

    // Check if any active pack is non-GPS (FREE, PREMIUM_ANNUAL, or PREMIUM_MONTHLY)
    const isNonGpsPlan = () => {
        const currentPacks = watchedPacks || [];
        if (currentPacks.length === 0) return true;

        const today = new Date();
        const activePacks = currentPacks.filter((pack: PackCompanyDto) => {
            if (!pack.date_start || !pack.expire) return false;
            const startDate = new Date(pack.date_start);
            const endDate = new Date(pack.expire);
            return startDate <= today && today <= endDate;
        });

        if (activePacks.length === 0) return true;

        return activePacks.every((pack: PackCompanyDto) =>
            pack.type === PackTypesEnum.FREE ||
            pack.type === PackTypesEnum.PREMIUM_ANNUAL ||
            pack.type === PackTypesEnum.PREMIUM_MONTHLY
        );
    };

    const hasGpsPlanWithoutLocator = (
        packsList: PackCompanyDto[] | undefined,
        locatorsList: GpsLocatorCompanyDto[] | undefined,
    ) => {
        const today = new Date();
        const hasActiveGpsPack = (packsList || []).some((pack: PackCompanyDto) => {
            if (!pack?.expire || !pack?.type) return false;
            const expire = new Date(pack.expire);
            if (!(expire > today)) return false;
            return pack.type === PackTypesEnum.PREMIUM_WITH_GPS_MONTHLY
                || pack.type === PackTypesEnum.PREMIUM_WITH_GPS_ANNUAL;
        });
        if (!hasActiveGpsPack) return false;
        const hasLocator = (locatorsList || []).some(l => !!l?.gpsLocatorId);
        return !hasLocator;
    };

    const getData = async () => {
        try {
            const isCreate = type === 'new'

            const [allPacksInfo, coutriesData, langs, allGpsLocators] = await Promise.all([
                CompaniesService.getPacksInfo(),
                CountriesService.catalog(),
                (isCreate || UsersService.isPlatformAdmin(userProfile?.roles)) ? LanguagesService.GetAllForSelectItem() : null,
                CompaniesService.getGpsLocators(),
            ]);

            setPlansList(allPacksInfo.map(x => {
                return { id: x.id, value: x.type, label: (t('plans.plans_types.' + x.type as any)) }
            }));


            setCountries(coutriesData.map(x => {
                return { value: x.name, label: (t('common.countries.' + x.name as any)) }
            }));
            setLanguages(langs);


            setGpsLocatorList(allGpsLocators.map(x => {
                return { value: x.id, label: x.name }
            }));

            if (id) {
                Loading.show();

                const result = await CompaniesService.getById(id);

                result.packs?.forEach((item: PackCompanyDto) => {
                    item.date_start = new Date(item.date_start)
                    item.expire = new Date(item.expire)
                })
                result.textMessagesPurchases?.forEach((item: TextMessagesPurchaseDto) => {
                    item.datePurchase = new Date(item.datePurchase)
                })
                setItem(result);

                companyForm.reset(result);
                Loading.hide();
            }

            setHasLoaded(true)

        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't get company information `, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
        }
    }

    useEffect(() => {
        getData();
    }, [id, type]);

    const navigateTo = (typeUrl?: string, id?: string) => {
        if (typeUrl) {
            history.push(`/companies/${typeUrl}/${id}`);
        } else {
            history.push(`/companies`);
        }
    }

    const onCancelRemove = () => {
        setItemToRemove(null);
        setShowRemoveModal(false);
    };

    const onRemove = async () => {
        if (itemToRemove === null) {
            addToast(t('common.messages.record_delete_error'), { appearance: 'error' });
            return;
        }

        try {
            await CompaniesService.remove(itemToRemove);
            onCancelRemove();
            navigateTo();
            addToast(t('common.messages.record_delete_success'), {
                appearance: 'success',
            });
        }
        catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't delete company`, error);
            addToast(t('common.messages.record_delete_error'), { appearance: 'error' });
        }
    };

    const showRemoveItemDialog = async (item: CompanyDto) => {
        setItemToRemove(item);
        setShowRemoveModal(true);
    }

    const onSubmit = async (companyForm: CompanyDto) => {
        try {
            if (!companyForm?.packs?.length) {
                return addToast(t('companies.required_plans_message'), { appearance: 'warning' });
            }

            if (hasGpsPlanWithoutLocator(companyForm.packs, companyForm.gpsLocators)) {
                return addToast(t('companies.gps_plan_requires_locator'), { appearance: 'warning' });
            }

            const totalSms = (companyForm.textMessagesPurchases || []).reduce((total: number, p: TextMessagesPurchaseDto) => total + (p?.numberSms || 0), 0);
            if ((item?.numberSmsUsed ?? 0) > totalSms) {
                return addToast(t('sms.total_less_than_used_warning'), { appearance: 'warning' });
            }

            Loading.show();
            const tempItem = { ...item };

            tempItem.name = companyForm.name;
            tempItem.taxNumber = companyForm.taxNumber;
            tempItem.email = item?.adminFieldsReadonly ? item?.email : companyForm.email;
            tempItem.adminName = item?.adminFieldsReadonly ? item?.adminName : companyForm.adminName;
            tempItem.phone = companyForm.phone;
            tempItem.website = companyForm.website;
            tempItem.addressLine = companyForm.addressLine;
            tempItem.zipCode = companyForm.zipCode;
            tempItem.city = companyForm.city;
            tempItem.country = companyForm.country;
            tempItem.removeAttachment = tempItem.logoId && medias.length === 0 ? true : false; 
            tempItem.useValueWithVat = companyForm.useValueWithVat;
            tempItem.hasExcelImportData = companyForm.hasExcelImportData ?? false;
            tempItem.languageId = companyForm.languageId;

            tempItem.maintenanceAttachmentsEmail = companyForm.maintenanceAttachmentsEmail;

            tempItem.packs = companyForm.packs;
            tempItem.gpsLocators = companyForm.gpsLocators;
            tempItem.textMessagesPurchases = companyForm.textMessagesPurchases;
            tempItem.reservationType = companyForm.reservationType;
            tempItem.dashboardGroup = companyForm.dashboardGroup;


            // try {
            //     await CompaniesService.validateVatNumber(form);

            // } catch (error) {                
            //     addToast(t('common.errors.invalid_tax_number'), { appearance: 'warning' });                
            //     Loading.hide();
            //     return;
            // }

            if (tempItem && tempItem.id != '') {
                await CompaniesService.update(tempItem, medias)
                navigateTo('details', tempItem.id);
            } else if (tempItem) {
                const id = await CompaniesService.create(tempItem, medias)
                navigateTo('details', id);
            }
            setItem(tempItem)
            setHasLoaded(false)
            Loading.hide();
            addToast(t('common.messages.record_save_success'), { appearance: 'success' });
        } catch (error) {
            if (error?.response?.status === 409) {
                addToast(t('common.messages.company_already_exists'), { appearance: 'warning' });
            }
            else if (error.message.includes('403')) {
                addToast(t('common.errors.tracking_auth'), { appearance: 'warning' });
                Logger.error(LOGGER_LOG_TYPE.CHARGES, `Provider Authentication Error - wrong credencials`, error);
            } else {
                addToast(t('common.messages.record_save_error'), { appearance: 'error' });
                Logger.error(LOGGER_LOG_TYPE.CHARGES, `Couldn't update the charge with id: ${companyForm.id}`, error);
            }

            Loading.hide();
        }
    }

    const onAddMedias = (medias: CustomFile[]) => {
        setMedias(medias);
    }

    const onRemoveMedia = (mediaId: string) => {
        const filteredMedia = [...medias.filter(x => x.id != mediaId)];
        setMedias(filteredMedia);
    }
 
    const onError = async () => {
        addToast(t('common.errors.required_fields_empty'), { appearance: 'warning' });
    };
 
    const getPageTitle = () => {
        return type === 'new' ? t('companies.new') : type === 'edit' ? t('companies.edit') : t('companies.details');
    }

    const removePlan = (index: number | null) => {
        if (index === null) return;
        packs.remove(index);
    }

    const onCancelRemovePlan = () => {
        setPlanToRemove(null);
        setShowRemovePlanModal(false);
    };

    const onRemovePlan = async () => {
        if (planToRemove === null) return;
        packs.remove(planToRemove);
        setShowRemovePlanModal(false)
    };

    const addNewPlan = () => {
        const newIndex = packs.fields.length;
        packs.append({});
        companyForm.setValue(`packs[${newIndex}]`, {
            packId: null,
            type: null,
            limit: null,
            usersLimit: null,
            date_start: null,
            expire: null,
            contractNumber: null,
        });
    };

    const removeGpsLocator = (index: number | null) => {
        if (index === null) return;
        gpsLocators.remove(index);
    }

    const addNewSmsPurchase = () => {
        const newIndex = textMessagesPurchases.fields.length;
        textMessagesPurchases.append({});
        companyForm.setValue(`textMessagesPurchases[${newIndex}]`, {
            numberSms: null,
            datePurchase: null,
            totalPrice: null,
        });
    };

    const onCancelRemoveSmsPurchase = () => {
        setSmsPurchaseToRemove(null);
        setShowRemoveSmsPurchaseModal(false);
    };

    const onRemoveSmsPurchase = async () => {
        if (smsPurchaseToRemove === null) return;
        textMessagesPurchases.remove(smsPurchaseToRemove);
        setShowRemoveSmsPurchaseModal(false);
    };

    const calculateTotalSms = () => {
        const purchases = watchedTextMessagesPurchases || [];
        return purchases.reduce((total: number, p: TextMessagesPurchaseDto) => total + (p?.numberSms || 0), 0);
    };


    const accountTab = () => (
        <form onSubmit={handleSubmit(onSubmit, onError)} id="company-form">
            <Box className={styles.box}>
                {hasLoaded && <div className={styles.form}>
                    <Row>
                        <Col xs={12} >
                            {/* Row 1: Nome, NIF, Telefone */}
                            <Row>
                                <Col xs={12} md={4} lg={4}>
                                    <FormItem>
                                        <Label className={styles.label}>
                                            {t('companies.name')}
                                            {!isDetails ? '*' : ''}
                                        </Label>
                                        <Input
                                            name='name'
                                            placeholder={t('companies.name')}
                                            defaultValue={item?.name}
                                            ref={register({ ...DEFAULT_INPUT_RULES_WITH_REQUIRED })}
                                            disabled={isDetails}
                                        />
                                        <InputError error={errors.name} />
                                    </FormItem>
                                </Col>
                                <Col xs={12} md={4} lg={4}>
                                    <FormItem>
                                        <Label className={styles.label}>
                                            {t('companies.tax_number')}
                                        </Label>
                                        <Input
                                            name='taxNumber'
                                            placeholder={t('companies.tax_number')}
                                            defaultValue={item?.taxNumber}
                                            ref={register({ ...DEFAULT_INPUT_RULES, maxLength: 100 })}
                                            disabled={isDetails}
                                        />
                                        <InputError error={errors.taxNumber} />
                                    </FormItem>
                                </Col>
                                <Col xs={12} md={4} lg={4}>
                                    <FormItem>
                                        <Label className={styles.label}>
                                            {t('companies.phone')}
                                        </Label>
                                        <Input
                                            name='phone'
                                            placeholder={t('companies.phone')}
                                            defaultValue={item?.phone}
                                            ref={register({ ...DEFAULT_INPUT_RULES, maxLength: 50 })}
                                            disabled={isDetails}
                                        />
                                        <InputError error={errors.phone} />
                                    </FormItem>
                                </Col>
                            </Row>
                            {/* Row 2: Morada, Código Postal, Localidade */}
                            <Row>
                                <Col xs={12} md={4} lg={4}>
                                    <FormItem>
                                        <Label className={styles.label}>
                                            {t('companies.address')}
                                            {!isDetails ? '*' : ''}
                                        </Label>
                                        <Input
                                            name='addressLine'
                                            placeholder={t('companies.address')}
                                            defaultValue={item?.addressLine}
                                            ref={register({ ...DEFAULT_INPUT_RULES_WITH_REQUIRED, maxLength: 200 })}
                                            disabled={isDetails}
                                        />
                                        <InputError error={errors.addressLine} />
                                    </FormItem>
                                </Col>
                                <Col xs={12} md={4} lg={4}>
                                    <FormItem>
                                        <Label className={styles.label}>
                                            {t('companies.zip_code')}
                                            {!isDetails ? '*' : ''}
                                        </Label>
                                        <Input
                                            name='zipCode'
                                            placeholder={t('companies.zip_code')}
                                            defaultValue={item?.zipCode}
                                            ref={register({ ...DEFAULT_INPUT_RULES_WITH_REQUIRED, maxLength: 15 })}
                                            disabled={isDetails}
                                        />
                                        <InputError error={errors.zipCode} />
                                    </FormItem>
                                </Col>
                                <Col xs={12} md={4} lg={4}>
                                    <FormItem>
                                        <Label className={styles.label}>
                                            {t('companies.city')}
                                        </Label>
                                        <Input
                                            name='city'
                                            placeholder={t('companies.city')}
                                            defaultValue={item?.city}
                                            ref={register({ ...DEFAULT_INPUT_RULES })}
                                            disabled={isDetails}
                                        />
                                        <InputError error={errors.city} />
                                    </FormItem>
                                </Col>
                            </Row>
                            {/* Row 3: País, Língua, Website */}
                            <Row>
                                <Col xs={12} md={4} lg={4}>
                                    <FormItem>
                                        <Label className={styles.label}>
                                            {t('common.country')}
                                        </Label>
                                        <SelectController
                                            form={companyForm}
                                            isClearable={true}
                                            name='country'
                                            options={countries}
                                            placeholder={t('common.country')}
                                            rules={{ required: false }}
                                            isDisabled={isDetails}
                                            filterOption={(candidate: any, input: any) => input ? removeAccents(candidate.label).toUpperCase().includes(removeAccents(input).toUpperCase()) : true}
                                        />
                                        <InputError error={errors.country} />
                                    </FormItem>
                                </Col>
                                {(type === 'new' && UsersService.isPlatformAdmin(userProfile?.roles)) && <Col xs={12} md={4} lg={4}>
                                    <FormItem>
                                        <Label className={styles.label}>
                                            {t('users.language')}
                                            {!isDetails ? '*' : ''}
                                        </Label>
                                        <SelectController
                                            form={companyForm}
                                            name="languageId"
                                            options={languages ? languages.map(x => { return { ...x, label: x.fullLabel ?? '' } }) : []}
                                            placeholder={t('users.language')}
                                            rules={{ required: true }}
                                            isDisabled={isDetails}
                                        />
                                        <InputError error={errors.languageId} />
                                    </FormItem>
                                </Col>}
                                <Col xs={12} md={4} lg={4}>
                                    <FormItem>
                                        <Label className={styles.label}>
                                            {t('companies.website')}
                                        </Label>
                                        <Input
                                            name='website'
                                            placeholder={t('companies.website')}
                                            defaultValue={item?.website}
                                            ref={register({ ...DEFAULT_INPUT_RULES })}
                                            disabled={isDetails}
                                        />
                                        <InputError error={errors.website} />
                                    </FormItem>
                                </Col>
                            </Row>
                            {/* Row 4: E-mail do Administrador, Nome do Administrador */}
                            <Row>
                                <Col xs={12} md={4} lg={4}>
                                    <FormItem>
                                        <Label className={styles.label}>
                                            {t('companies.admin_email')}
                                            {!isDetails && !item?.adminFieldsReadonly ? '*' : ''}
                                        </Label>
                                        <div
                                            data-tip={item?.adminFieldsReadonly && item?.currentAdminEmail ? t('companies.admin_changed_tooltip' as any, { email: item.currentAdminEmail }) as string : undefined}
                                            data-for={item?.adminFieldsReadonly ? 'adminChangedTooltip' : undefined}
                                        >
                                            <Input
                                                name='email'
                                                placeholder={t('companies.admin_email')}
                                                defaultValue={item?.email}
                                                type={'email'}
                                                ref={register(item?.adminFieldsReadonly ? {} : { ...DEFAULT_EMAIL_RULES_WITH_REQUIRED, maxLength: 100 })}
                                                disabled={isDetails || item?.adminFieldsReadonly}
                                            />
                                        </div>
                                        <InputError error={errors.email} />
                                    </FormItem>
                                </Col>
                                <Col xs={12} md={4} lg={4}>
                                    <FormItem>
                                        <Label className={styles.label}>
                                            {t('companies.admin_name')}
                                            {!isDetails && !item?.adminFieldsReadonly ? '*' : ''}
                                        </Label>
                                        <div
                                            data-tip={item?.adminFieldsReadonly && item?.currentAdminEmail ? t('companies.admin_changed_tooltip' as any, { email: item.currentAdminEmail }) as string : undefined}
                                            data-for={item?.adminFieldsReadonly ? 'adminChangedTooltip' : undefined}
                                        >
                                            <Input
                                                name='adminName'
                                                placeholder={t('companies.admin_name')}
                                                defaultValue={item?.adminName}
                                                ref={register(item?.adminFieldsReadonly ? {} : { ...DEFAULT_INPUT_RULES_WITH_REQUIRED, maxLength: 200 })}
                                                disabled={isDetails || item?.adminFieldsReadonly}
                                            />
                                        </div>
                                        <InputError error={errors.adminName} />
                                    </FormItem>
                                </Col>
                            </Row>
                            {item?.adminFieldsReadonly && (
                                <ReactTooltip id='adminChangedTooltip' type="dark" effect="solid" />
                            )}

                            <Separator />
                            <Row >
                                <Col xs={12} xl={4} lg={4}>
                                    <FormItem>
                                        <Label className={styles.label}>{t('account.maintenance_attachments_email')}</Label>
                                        <Input
                                            name='maintenanceAttachmentsEmail'
                                            placeholder={t('companies.email')}
                                            defaultValue={item?.maintenanceAttachmentsEmail}
                                            ref={companyForm.register({ ...DEFAULT_INPUT_RULES })}
                                            disabled={isDetails}
                                        />
                                    </FormItem>
                                </Col>
                            </Row>

                            <Separator />
                            <Row>
                                <Col xs={12}>
                                    <FormItem>
                                        <CheckBoxController form={companyForm as any}
                                            name={`useValueWithVat`}
                                            label={t('companies.use_value_with_vat')}
                                            disabled={isDetails} />
                                    </FormItem>
                                </Col>
                            </Row>
                            <Row>
                                <Col xs={12}>
                                    <FormItem>
                                        <div className={styles.checkboxWithInfo}>
                                            <CheckBoxController form={companyForm as any}
                                                name={`hasExcelImportData`}
                                                label={t('companies.has_excel_import_data')}
                                                disabled={isDetails} />
                                            <FaInfoCircle
                                                data-tip={t('companies.has_excel_import_data_tooltip')}
                                                data-for={'hasExcelImportDataTooltip'}
                                                className={styles.infoIcon}
                                            />
                                            <ReactTooltip id={'hasExcelImportDataTooltip'} type="dark" effect="solid" html={true} />
                                        </div>
                                    </FormItem>
                                </Col>
                            </Row>
                            {UsersService.isPlatformAdmin(userProfile?.roles) && (
                                <>
                                    <Separator />
                                    <Row>
                                        <Col xs={12} xl={4}>
                                            <FormItem>
                                                <Label className={styles.label}>
                                                    {t('requests.config.type_label')}
                                                </Label>
                                                <SelectController
                                                    form={companyForm}
                                                    name="reservationType"
                                                    options={reservationTypeOptions}
                                                    placeholder={t('common.select_option')}
                                                    rules={{ required: false }}
                                                    isDisabled={isDetails}
                                                />
                                            </FormItem>
                                        </Col>
                                        <Col xs={12} xl={8}>
                                            {watchedReservationType && (
                                                <div className={styles.reservationTypeInfo}>
                                                    <FaInfoCircle className={styles.reservationTypeInfoIcon} />
                                                    <span>{getReservationTypeDescription(watchedReservationType)}</span>
                                                </div>
                                            )}
                                        </Col>
                                    </Row>
                                    <Separator />
                                    <DashboardGroupField form={companyForm} isDisabled={isDetails} />
                                </>
                            )}
                            <Separator />
                            <Row>
                                <Col xs={12}>
                                    <div className={styles.headerPlans}>
                                        <div className={styles.headerText}>
                                            {t('plans.plans')}
                                        </div>
                                        {!isDetails && <div>
                                            <span className={styles.headerButton} onClick={() => addNewPlan()}>
                                                <span className={styles.headerButtonText}></span>
                                                <img src={AddIcon} className={styles.headerButtonIcon} />
                                            </span>
                                        </div>}
                                    </div>
                                </Col>
                            </Row>
                            <Row style={{ marginTop: '15px' }}>
                                <Col xs={12}>
                                    {packs.fields.map((ti, index) => {
                                        return (
                                            <PlanFormItem
                                                key={`${ti.formId}_${index}`}
                                                form={companyForm}
                                                isDetails={isDetails}
                                                onRemoveClick={() => {
                                                    setPlanToRemove(index);
                                                    setShowRemovePlanModal(true);
                                                }}
                                                index={index}
                                                plansList={plansList}
                                                setRender={() => { setReRender(reRender + 1); }}
                                            />
                                        );
                                    })}
                                </Col>
                            </Row>


   <Separator />
                            <Row>
                                <Col xs={12}>
                                    <div className={styles.headerPlans}>
                                        <div className={styles.headerText}>
                                            {t('sms.purchases_title')}

                                            {isNonGpsPlan() && (
                                            <span className={styles.smsWarning}>
                                                {t('sms.non_gps_plan_warning')}
                                            </span>
                                            )}
                                        </div>
                                        
                                        {!isDetails && <div>
                                            <span className={styles.headerButton} onClick={() => addNewSmsPurchase()}>
                                                <span className={styles.headerButtonText}></span>
                                                <img src={AddIcon} className={styles.headerButtonIcon} />
                                            </span>
                                        </div>}
                                    </div>
                                </Col>
                            </Row>
                            <Row style={{ marginTop: '15px' }}>
                                <Col xs={12}>
                                    {textMessagesPurchases.fields.map((ti, index) => {
                                        return (
                                            <SmsFormItem
                                                key={`sms_${ti.formId}_${index}`}
                                                form={companyForm}
                                                isDetails={isDetails}
                                                onRemoveClick={() => {
                                                    setSmsPurchaseToRemove(index);
                                                    setShowRemoveSmsPurchaseModal(true);
                                                }}
                                                index={index}
                                            />
                                        );
                                    })}
                                </Col>
                            </Row>
                            <Row style={{ marginTop: '15px' }}>
                                <Col xs={12} md={4} lg={4}>
                                    <FormItem>
                                        <Label className={styles.label}>
                                            {t('sms.total_sms')}
                                        </Label>
                                        <Input
                                            type='number'
                                            value={calculateTotalSms()}
                                            disabled={true}
                                        />
                                    </FormItem>
                                </Col>
                                <Col xs={12} md={4} lg={4}>
                                    <FormItem>
                                        <Label className={styles.label}>
                                            {t('sms.sms_used')}
                                        </Label>
                                        <Input
                                            type='number'
                                            value={item?.numberSmsUsed ?? 0}
                                            disabled={true}
                                        />
                                    </FormItem>
                                </Col>
                                <Col xs={12} md={4} lg={4}>
                                    <FormItem>
                                        <Label className={styles.label}>
                                            {t('sms.sms_left')}
                                        </Label>
                                        <Input
                                            type='number'
                                            value={calculateTotalSms() - (item?.numberSmsUsed ?? 0)}
                                            disabled={true}
                                        />
                                    </FormItem>
                                </Col>
                            </Row>


                            <Separator />
                            <Row>
                                <Col xs={12}>
                                    <div className={styles.headerPlans}>
                                        <div className={styles.headerText}>
                                            {t('gps_locator.title')}
                                        </div>
                                        {!isDetails && <div>
                                            <span className={styles.headerButton} onClick={() => gpsLocators.append({ id: '', gpsLocatorId: '', username: '', password: '' })}>
                                                <span className={styles.headerButtonText}></span>
                                                <img src={AddIcon} className={styles.headerButtonIcon} />
                                            </span>
                                        </div>}
                                    </div>
                                </Col>
                            </Row>
                            <Row style={{ marginTop: '15px' }}>
                                <Col xs={12}>
                                    {gpsLocators.fields.map((ti, index) => {
                                        return (
                                            <GpsLocatorFormItem
                                                key={`gl_${ti.formId}`}
                                                form={companyForm}
                                                isDetails={isDetails}
                                                onRemoveClick={() => {
                                                    removeGpsLocator(index);
                                                }}
                                                index={index}
                                                gpsLocatorList={gpsLocatorList}
                                                setRender={() => { setReRender(reRender + 1); }}
                                            />
                                        );
                                    })}
                                </Col>
                            </Row>







                        </Col>
                    </Row>



                    <div className={styles.buttonsContainer}>
                        <Button
                            preset={'secondary'}
                            type="button"
                            onClick={() => navigateTo()}
                            text={t('common.cancel')}
                        />
                        {isDetails && hasCompaniesWritePolicy &&
                            <Button
                                type="button"
                                text={t('common.remove')}
                                preset={'danger'}
                                onClick={() => showRemoveItemDialog({ id: item?.id } as CompanyDto)} />
                        }
                        {isDetails && hasCompaniesWritePolicy &&
                            <Button
                                type={'button'}
                                text={t('common.edit')}
                                onClick={() => { navigateTo('edit', item?.id); }}
                            />}
                        {!isDetails && hasCompaniesWritePolicy &&
                            <Button
                                type={'submit'}
                                text={t('common.save')}
                                form="company-form"
                            />}

                        <QuestionYesNo onNo={onCancelRemovePlan} onYes={onRemovePlan} isVisible={showRemovePlanModal} message={t('plans.remove_plan_question')} />
                        <QuestionYesNo onNo={onCancelRemoveSmsPurchase} onYes={onRemoveSmsPurchase} isVisible={showRemoveSmsPurchaseModal} message={t('sms.remove_purchase_question')} />
                        <QuestionYesNo onNo={onCancelRemove} onYes={onRemove} isVisible={showRemoveModal} message={t('common.messages.remove_record_with_ident', { name: '' })} />

                    </div>
                </div>}
            </Box>
        </form>
    );

    const packsTab = () => (
        <PlansScreen
            isDetails={isDetails}
            companyId={id}
            issues={[]}
        ></PlansScreen>)



    const tabs: TabItem[] = [
        {
            id: 'account',
            title: t('companies.account'),
            content: accountTab()
        },
        {
            id: 'subscription',
            title: t('companies.plans'),
            content: packsTab()
        },
        {
            id: 'roles',
            title: t('roles.title'),
            content: tabId === 'roles' && tabType != undefined ? <RolesDetails /> : <RolesList isAdmin={false} companyId={item.id} />
        },
        {
            id: 'rules',
            title: t('rules.title'),
            content: <RulesTab isAdmin={false} companyId={item.id} />
        },
        {
            id: 'additionaldata',
            title: t('additional_data.title'),
            content: tabId === 'additionaldata' && tabType != undefined ? <AdditionalDataDetails /> : <AdditionalDataList companyId={item.id ?? ''} />
        }
    ];


    return (

        <ScreenTitle title={getPageTitle()}>
            <CompactPageLayout title={getPageTitle() as string} contentAreaClassName={compactPageLayoutStyles.scrollableContent}>
                {type !== 'new' && <Tabs
                    items={tabs}
                    activeTabId={activeTabId}
                    onChange={(tId: string) => { setActiveTabId(tId) }} />
                }
                {type === 'new' && accountTab()
                }
            </CompactPageLayout>
        </ScreenTitle>

    );
};

export default CompanyScreen;
